import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Add,
	Clear,
	Close,
	Search,
	UnfoldLess,
	UnfoldMore,
} from "@mui/icons-material";
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	Drawer,
	Fade,
	Grid,
	IconButton,
	InputAdornment,
	Paper,
	Snackbar,
	Stack,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";

import HierarchyNode, { treeMutators } from "./hierarchyNode";
import TemplateForm from "./templateForm";
import { FabricanteSummary, FuncaoSummary } from "./templateSummary";
import InternoModalConfirmacaoSaida from "../../components/modalSaindoDaPagina";
import api from "../../services/api";
import LoaderThreeDots from "../../components/loadingAnimation";
import SchemaBulkApply from "./schemaBulkApply";
import {
	denormalizeSchema,
	emptyDraft,
	normalizeSchema,
	schemaDraftKey,
} from "../../utils/dashboardSchemaDraft";
import { filterFabricantes } from "../../utils/predefinicoesTree";
import stylesFn from "./styles";

const EMPTY_MACROS = [{ macro: "", valor: "", tag: "" }];

// parse seguro: se vier string JSON inválida não derruba a página
function safeParse(value, fallback) {
	if (value == null) return fallback;
	if (typeof value !== "string") return value;
	try {
		return JSON.parse(value);
	} catch {
		return fallback;
	}
}

function flattenPredefinicoes(fabricantesArr) {
	const out = [];
	for (const fab of fabricantesArr || []) {
		for (const fun of fab.Funcoes || []) {
			for (const mod of fun.Modelos || []) {
				if (mod.Predefinicoes) out.push(mod.Predefinicoes);
			}
		}
	}
	return out;
}

// localiza o Modelo cuja Predefinicoes.id corresponde ao id passado
function findModeloByPredefinicaoId(fabricantesArr, predefinicaoId) {
	for (const fab of fabricantesArr || []) {
		for (const fun of fab.Funcoes || []) {
			for (const mod of fun.Modelos || []) {
				if (mod?.Predefinicoes?.id === predefinicaoId) return mod;
			}
		}
	}
	return null;
}

const normalizeText = (text) =>
	(text || "")
		.toLowerCase()
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "");

export default function Templates() {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));
	const styles = useMemo(() => stylesFn(theme), [theme]);

	const [fabricantes, setFabricantes] = useState([]);
	const [predefinicoes, setPredefinicoes] = useState([]);
	const [templatesList, setTemplatesList] = useState([]);

	const [filtro, setFiltro] = useState("");
	const [expanded, setExpanded] = useState([]);
	const [selected, setSelected] = useState(null);

	const toggleExpand = (id) => {
		setExpanded((prev) =>
			prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
		);
	};

	const [predefinicaoId, setPredefinicaoId] = useState("");
	const [nomeModelo, setNomeModelo] = useState("selecione o modelo");

	// estado do formulário
	const [groupHosts, setGroupHosts] = useState([]);
	const [templates, setTemplates] = useState([]);
	const [macros, setMacros] = useState(EMPTY_MACROS);
	const [lastGroupHosts, setLastGroupHosts] = useState([]);
	const [lastTemplates, setLastTemplates] = useState([]);
	const [lastMacros, setLastMacros] = useState(EMPTY_MACROS);

	const [ssh, setSsh] = useState(false);
	const [userSSH, setUserSSH] = useState("");
	const [portaSSH, setPortaSSH] = useState("");
	const [senhaSSH, setSenhaSSH] = useState("");
	const [telnet, setTelnet] = useState(false);
	const [userTelnet, setUserTelnet] = useState("");
	const [portaTelnet, setPortaTelnet] = useState("");
	const [senhaTelnet, setSenhaTelnet] = useState("");
	const [lastConfig, setLastConfig] = useState(null);

	// dashboard_schema (vinculado ao Modelo); dashboardSchema é o último SALVO
	const [modeloId, setModeloId] = useState(null);
	const [dashboardSchema, setDashboardSchema] = useState(null);
	const [schemaDraft, setSchemaDraft] = useState(emptyDraft);
	const [lastSchemaDraft, setLastSchemaDraft] = useState(emptyDraft);
	// saída de emergência: edição do JSON cru
	const [schemaAvancado, setSchemaAvancado] = useState(false);
	const [schemaAvancadoTexto, setSchemaAvancadoTexto] = useState("");
	const [schemaAvancadoErro, setSchemaAvancadoErro] = useState(null);
	// aplicação em massa
	const [openBulkSchema, setOpenBulkSchema] = useState(false);

	// documentacao em markdown (vinculada ao Modelo)
	const [documentacao, setDocumentacao] = useState("");
	const [lastDocumentacao, setLastDocumentacao] = useState("");

	const [editar, setEditar] = useState(false);
	const [salvando, setSalvando] = useState(false);
	const [openAddFabricante, setOpenAddFabricante] = useState(false);
	const [fabricante, setFabricante] = useState("");
	const [snackbar, setSnackbar] = useState(null);

	// abas
	const [activeTab, setActiveTab] = useState(0);

	// loading e transições
	const [loading, setLoading] = useState(true);
	const [selectionLoading, setSelectionLoading] = useState(false);

	// modal de alterações não salvas
	const [openExitModal, setOpenExitModal] = useState(false);
	const pendingNavigationRef = useRef(null);

	// seleção de Fabricante (mostra sumário no painel direito)
	const [selectedFabricanteId, setSelectedFabricanteId] = useState(null);
	// seleção de Função (mostra sumário no painel direito)
	const [selectedFuncaoId, setSelectedFuncaoId] = useState(null);

	// dirty flags reportados pelos sumários (modo edição com draft pendente)
	const [funcoesSummaryDirty, setFuncoesSummaryDirty] = useState(false);
	const [modelosSummaryDirty, setModelosSummaryDirty] = useState(false);

	// sinal incrementado para forçar os sumários a descartarem o draft
	// (ex.: usuário confirmou "Descartar e continuar" no modal de saída)
	const [summaryResetSignal, setSummaryResetSignal] = useState(0);

	const fabricantesFiltrados = useMemo(
		() => filterFabricantes(fabricantes, filtro),
		[fabricantes, filtro]
	);

	const fabricanteSelecionado = useMemo(
		() =>
			selectedFabricanteId
				? fabricantes.find((f) => f.id === selectedFabricanteId) || null
				: null,
		[fabricantes, selectedFabricanteId]
	);

	const funcaoSelecionada = useMemo(() => {
		if (!selectedFuncaoId) return null;
		for (const fab of fabricantes) {
			for (const fun of fab.Funcoes || []) {
				if (fun?.id === selectedFuncaoId) {
					return { funcao: fun, fabricante: fab };
				}
			}
		}
		return null;
	}, [fabricantes, selectedFuncaoId]);

	const modeloSelecionado = useMemo(() => {
		if (!predefinicaoId) return null;
		for (const fab of fabricantes) {
			for (const fun of fab.Funcoes || []) {
				for (const mod of fun.Modelos || []) {
					if (mod?.Predefinicoes?.id === predefinicaoId) {
						return { modelo: mod, fabricante: fab, funcao: fun };
					}
				}
			}
		}
		return null;
	}, [fabricantes, predefinicaoId]);

	const selecionado =
		Boolean(predefinicaoId) ||
		Boolean(selectedFabricanteId) ||
		Boolean(selectedFuncaoId);

	// snapshot serializado para detectar dirty
	const dirtyFingerprint = useCallback(
		(g, t, m, s, sshOn, sshU, sshP, sshS, telOn, telU, telP, telS, doc) =>
			JSON.stringify({
				g,
				t,
				m,
				s,
				sshOn,
				sshU,
				sshP,
				sshS,
				telOn,
				telU,
				telP,
				telS,
				doc,
			}),
		[]
	);

	const hasUnsavedChanges = useMemo(() => {
		if (funcoesSummaryDirty || modelosSummaryDirty) return true;
		if (!editar) return false;
		if (schemaAvancadoErro) return true; // JSON quebrado conta como pendente
		const current = dirtyFingerprint(
			groupHosts,
			templates,
			macros,
			schemaDraftKey(schemaDraft),
			ssh,
			userSSH,
			portaSSH,
			senhaSSH,
			telnet,
			userTelnet,
			portaTelnet,
			senhaTelnet,
			documentacao
		);
		const original = dirtyFingerprint(
			lastGroupHosts,
			lastTemplates,
			lastMacros,
			schemaDraftKey(lastSchemaDraft),
			lastConfig?.ssh ?? false,
			lastConfig?.userSSH ?? "",
			lastConfig?.portaSSH ?? "",
			lastConfig?.senhaSSH ?? "",
			lastConfig?.telnet ?? false,
			lastConfig?.userTelnet ?? "",
			lastConfig?.portaTelnet ?? "",
			lastConfig?.senhaTelnet ?? "",
			lastDocumentacao
		);
		return current !== original;
	}, [
		funcoesSummaryDirty,
		modelosSummaryDirty,
		editar,
		dirtyFingerprint,
		groupHosts,
		templates,
		macros,
		schemaDraft,
		schemaAvancadoErro,
		ssh,
		userSSH,
		portaSSH,
		senhaSSH,
		telnet,
		userTelnet,
		portaTelnet,
		senhaTelnet,
		documentacao,
		lastGroupHosts,
		lastTemplates,
		lastMacros,
		lastSchemaDraft,
		lastConfig,
		lastDocumentacao,
	]);

	// aviso ao recarregar/fechar o navegador
	useEffect(() => {
		const handleBeforeUnload = (e) => {
			if (hasUnsavedChanges) {
				e.preventDefault();
				e.returnValue = "";
			}
		};
		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => window.removeEventListener("beforeunload", handleBeforeUnload);
	}, [hasUnsavedChanges]);

	const handleNavigation = useCallback(
		(action) => {
			if (hasUnsavedChanges) {
				pendingNavigationRef.current = action;
				setOpenExitModal(true);
			} else {
				action?.();
			}
		},
		[hasUnsavedChanges]
	);

	const confirmExit = () => {
		setEditar(false);
		// força os sumários a descartarem qualquer draft em aberto
		setSummaryResetSignal((v) => v + 1);
		setFuncoesSummaryDirty(false);
		setModelosSummaryDirty(false);
		setOpenExitModal(false);
		const action = pendingNavigationRef.current;
		pendingNavigationRef.current = null;
		action?.();
	};

	const cancelExit = () => {
		setOpenExitModal(false);
		pendingNavigationRef.current = null;
	};

	// --------- mutadores recebidos pela árvore ---------
	function createFabricantes(novo) {
		setFabricantes((prev) => [...prev, novo]);
	}
	function updateFabricantes(novo) {
		setFabricantes((prev) =>
			prev.map((f) => (f.id === novo.id ? { ...f, ...novo } : f))
		);
	}
	function deleteFabricante(removeId) {
		setFabricantes((prev) =>
			prev.map((f) =>
				f.id !== removeId ? f : { id: null, fabricante: null, Funcoes: [] }
			)
		);
		setPredefinicaoId("");
	}
	function updatePredefinicoes(nova) {
		setPredefinicoes((prev) => prev.map((p) => (p.id === nova.id ? nova : p)));
	}
	function createPredefinicoes(nova) {
		if (!nova) return;
		setPredefinicoes((prev) => [...prev, nova]);
	}

	// envolvendo seleção/criação para respeitar dirty
	const selectPredefinicao = useCallback(
		(novoId, novoNome) => {
			handleNavigation(() => {
				setSelectedFabricanteId(null);
				setSelectedFuncaoId(null);
				setPredefinicaoId(novoId);
				if (novoNome) setNomeModelo(novoNome);
				setActiveTab(0);
				setEditar(false);
			});
		},
		[handleNavigation]
	);

	const selectFabricante = useCallback(
		(fabId) => {
			handleNavigation(() => {
				setPredefinicaoId("");
				setNomeModelo("selecione o modelo");
				setEditar(false);
				setSelectedFuncaoId(null);
				setSelectedFabricanteId(fabId);
			});
		},
		[handleNavigation]
	);

	const selectFuncao = useCallback(
		(funId) => {
			handleNavigation(() => {
				setPredefinicaoId("");
				setNomeModelo("selecione o modelo");
				setEditar(false);
				setSelectedFabricanteId(null);
				setSelectedFuncaoId(funId);
			});
		},
		[handleNavigation]
	);

	const expandeNovoModelo = useCallback(
		(novoModelo, funcaoId) => {
			handleNavigation(() => {
				setExpanded((prev) =>
					prev.includes(String(funcaoId)) ? prev : [...prev, String(funcaoId)]
				);
				setSelected(String(novoModelo.id));
				setPredefinicaoId(novoModelo.Predefinicoes.id);
				setNomeModelo(novoModelo.modelo);
				setActiveTab(0);
			});
		},
		[handleNavigation]
	);

	const expandeNovaFuncao = useCallback((novoValor) => {
		setExpanded((prev) =>
			prev.includes(String(novoValor.id)) ? prev : [...prev, String(novoValor.id)]
		);
		setSelected(String(novoValor.id));
	}, []);

	// --------- contexto compartilhado para o componente recursivo ---------
	const treeCtx = useMemo(() => {
		const mutators = treeMutators(setFabricantes);
		return {
			expandedIds: expanded,
			selectedId: predefinicaoId,
			selectedFabricanteId,
			selectedFuncaoId,
			toggleExpand,
			expand: (id) =>
				setExpanded((prev) =>
					prev.includes(id) ? prev : [...prev, id]
				),
			selectModelo: selectPredefinicao,
			selectFabricante,
			selectFuncao,
			clearSelection: () => {
				setPredefinicaoId("");
				setNomeModelo("selecione o modelo");
				setSelectedFabricanteId(null);
				setSelectedFuncaoId(null);
			},
			createPredefinicoes,
			notify: (msg, severity = "success") =>
				setSnackbar({ children: msg, severity }),
			mutators,
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [expanded, predefinicaoId, selectedFabricanteId, selectedFuncaoId, selectPredefinicao, selectFabricante, selectFuncao]);

	// --------- macros ---------
	const handleMacroChange = (index, event) => {
		const { name, value } = event.target;
		setMacros((prev) =>
			prev.map((item, i) => (i === index ? { ...item, [name]: value } : item))
		);
	};
	const addMacro = () =>
		setMacros((prev) => [...prev, { macro: "", valor: "", tag: "" }]);
	const removeMacro = (index) =>
		setMacros((prev) => prev.filter((_, i) => i !== index));

	// --------- expansão / seleção (botão expandir/recolher tudo) ---------
	const allExpandedIds = useMemo(() => {
		const all = [];
		for (const fab of fabricantes) {
			all.push(String(fab.id));
			for (const fun of fab.Funcoes || []) {
				all.push(String(fun.id));
			}
		}
		return all;
	}, [fabricantes]);

	const allExpanded =
		allExpandedIds.length > 0 && expanded.length >= allExpandedIds.length;

	const handleToggleAll = () => {
		setExpanded(allExpanded ? [] : allExpandedIds);
	};

	// --------- carrega dados ao selecionar predefinição ---------
	// IMPORTANTE: este effect só deve rodar quando o usuário troca de modelo.
	// Mudanças em `predefinicoes` ou `fabricantes` (por exemplo, depois de um
	// save parcialmente bem-sucedido) NÃO devem sobrescrever o que está sendo
	// editado — caso contrário, uma falha no meio do save "limpa" o formulário.
	const lastLoadedPredefinicaoIdRef = useRef(null);
	useEffect(() => {
		if (!predefinicaoId) {
			lastLoadedPredefinicaoIdRef.current = null;
			return;
		}
		// só recarrega se o id realmente mudou
		if (lastLoadedPredefinicaoIdRef.current === predefinicaoId) return;
		const pre = predefinicoes.find(({ id }) => id === predefinicaoId);
		if (!pre) return;
		lastLoadedPredefinicaoIdRef.current = predefinicaoId;

		// loading mínimo p/ evitar flicker
		setSelectionLoading(true);
		const start = Date.now();

		const novosTemplates = safeParse(pre.templates, []);
		const novosGrupos = safeParse(pre.groups, []);
		const novosMacros = safeParse(pre.macros, EMPTY_MACROS) || EMPTY_MACROS;

		setTemplates(novosTemplates);
		setGroupHosts(novosGrupos);
		setMacros(novosMacros);
		setLastTemplates(novosTemplates);
		setLastGroupHosts(novosGrupos);
		setLastMacros(novosMacros);

		const config = pre.config || {};
		const sshOn = !!config.SSH;
		const telOn = !!config.Telnet;
		const sshU = config.SSH?.userSSH || "";
		const sshP = config.SSH?.portaSSH || "";
		const sshS = config.SSH?.senhaSSH || "";
		const telU = config.Telnet?.userTelnet || "";
		const telP = config.Telnet?.portaTelnet || "";
		const telS = config.Telnet?.senhaTelnet || "";
		setSsh(sshOn);
		setUserSSH(sshU);
		setPortaSSH(sshP);
		setSenhaSSH(sshS);
		setTelnet(telOn);
		setUserTelnet(telU);
		setPortaTelnet(telP);
		setSenhaTelnet(telS);
		setLastConfig({
			ssh: sshOn,
			userSSH: sshU,
			portaSSH: sshP,
			senhaSSH: sshS,
			telnet: telOn,
			userTelnet: telU,
			portaTelnet: telP,
			senhaTelnet: telS,
		});

		// schema
		const mod = findModeloByPredefinicaoId(fabricantes, predefinicaoId);
		const schemaObj = safeParse(mod?.dashboard_schema, null);
		const draft = normalizeSchema(schemaObj);
		setModeloId(mod?.id || null);
		setDashboardSchema(schemaObj);
		setSchemaDraft(draft);
		setLastSchemaDraft(draft);
		setSchemaAvancado(false);
		setSchemaAvancadoTexto("");
		setSchemaAvancadoErro(null);

		const docStr = mod?.documentacao || "";
		setDocumentacao(docStr);
		setLastDocumentacao(docStr);

		const MIN_LOADING = 250;
		const elapsed = Date.now() - start;
		const t = setTimeout(
			() => setSelectionLoading(false),
			Math.max(0, MIN_LOADING - elapsed)
		);
		return () => clearTimeout(t);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [predefinicaoId]);

	// expande automaticamente fabricantes/funções que correspondem à busca
	useEffect(() => {
		const q = normalizeText(filtro);
		if (!q) return;
		const ids = [];
		for (const fab of fabricantes) {
			let pushFab = false;
			for (const fun of fab.Funcoes || []) {
				const funMatch = normalizeText(fun.funcao || fun.nome).includes(q);
				const modelMatch = (fun.Modelos || []).some((m) =>
					normalizeText(m.modelo).includes(q)
				);
				if (funMatch || modelMatch) {
					ids.push(String(fun.id));
					pushFab = true;
				}
			}
			if (pushFab || normalizeText(fab.fabricante).includes(q)) {
				ids.push(String(fab.id));
			}
		}
		setExpanded((prev) => [...new Set([...prev, ...ids])]);
	}, [filtro, fabricantes]);

	// --------- API ---------
	useEffect(() => {
		(async () => {
			setLoading(true);
			await Promise.all([handleApiGetPredefinicoes(), handleApiGetTemplates()]);
			setLoading(false);
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	async function handleApiGetPredefinicoes() {
		try {
			const response = await api.get("/getPredefinicoes");
			if (response.data.status === "Error") {
				setSnackbar({
					children: "Error: Não foi possível buscar Predefinicoes",
					severity: "error",
				});
			} else {
				setFabricantes(response.data || []);
				setPredefinicoes(flattenPredefinicoes(response.data || []));
			}
		} catch (error) {
			console.error(error);
			setSnackbar({
				children: "Error: Não foi possível se conectar com o servidor",
				severity: "error",
			});
		}
	}

	async function handleApiGetTemplates() {
		try {
			const response = await api.get("/getTemplates");
			if (response.data.status === "Error") {
				setSnackbar({
					children: "Error: Não foi possível buscar templates",
					severity: "error",
				});
			} else {
				setTemplatesList(response.data || []);
			}
		} catch (error) {
			console.error(error);
			setSnackbar({
				children: "Error: Não foi possível se conectar com o servidor",
				severity: "error",
			});
		}
	}

	async function handleEditaPredefinicoes() {
		if (salvando) return;

		if (schemaAvancadoErro) {
			setSnackbar({
				children: "Error: corrija o JSON do schema antes de salvar",
				severity: "error",
			});
			setActiveTab(1);
			return;
		}

		const schemaToSave = denormalizeSchema(schemaDraft);

		setSalvando(true);
		try {
			// 1) Salva as predefinições
			const response = await api.put("/putPredefinicao", {
				id: predefinicaoId,
				templates: JSON.stringify(templates),
				macros: JSON.stringify(macros),
				groups: JSON.stringify(groupHosts),
				config: {
					SSH: ssh ? { userSSH, senhaSSH, portaSSH } : null,
					Telnet: telnet ? { userTelnet, senhaTelnet, portaTelnet } : null,
				},
			});
			if (!response?.data || response.data.status === "Error") {
				setSnackbar({
					children:
						"Error: Não foi possível salvar as predefinicoes, verifique se você selecionou um modelo corretamente",
					severity: "error",
				});
				return;
			}
			const dataPredef = response.data;

			// 2) Salva o schema + documentação do Modelo (se houver)
			let respModeloData = null;
			if (modeloId) {
				let respModelo;
				try {
					respModelo = await api.put("/putModelo", {
						id: modeloId,
						modelo: nomeModelo,
						dashboard_schema: schemaToSave,
						documentacao,
					});
				} catch (err) {
					console.error(err);
					setSnackbar({
						children:
							"Error: Não foi possível salvar o schema do modelo. Suas alterações foram preservadas.",
						severity: "error",
					});
					return;
				}
				if (!respModelo?.data || respModelo.data.status === "Error") {
					setSnackbar({
						children:
							"Error: Não foi possível salvar o schema do modelo. Suas alterações foram preservadas.",
						severity: "error",
					});
					return;
				}
				respModeloData = respModelo.data;
			}

			// 3) Tudo OK — agora sim aplica os updates locais
			updatePredefinicoes(dataPredef);
			if (modeloId) {
				// _version vem do servidor, que é quem versiona
				const schemaSalvo =
					safeParse(respModeloData?.dashboard_schema, null) ?? schemaToSave;

				setFabricantes((prev) =>
					prev.map((fab) => ({
						...fab,
						Funcoes: (fab.Funcoes || []).map((fun) => ({
							...fun,
							Modelos: (fun.Modelos || []).map((mod) =>
								mod.id === modeloId
									? {
										...mod,
										dashboard_schema: schemaSalvo,
										documentacao,
									}
									: mod
							),
						})),
					}))
				);
				setDashboardSchema(schemaSalvo);
				setLastSchemaDraft(schemaDraft);
				setSchemaAvancadoTexto(
					schemaSalvo ? JSON.stringify(schemaSalvo, null, 2) : ""
				);
				setLastDocumentacao(documentacao);
			}

			setSnackbar({ children: "Dados salvos com sucesso", severity: "success" });
			setLastGroupHosts(groupHosts);
			setLastTemplates(templates);
			setLastMacros(macros);
			setLastConfig({
				ssh,
				userSSH,
				portaSSH,
				senhaSSH,
				telnet,
				userTelnet,
				portaTelnet,
				senhaTelnet,
			});
			setEditar(false);
			// marca este id como já carregado para o effect não re-disparar
			lastLoadedPredefinicaoIdRef.current = predefinicaoId;
		} catch (error) {
			console.error(error);
			setSnackbar({
				children: "Error: Não foi possível se conectar com o servidor",
				severity: "error",
			});
		} finally {
			setSalvando(false);
		}
	}

	async function handleApiCriaFabricante() {
		const nome = fabricante.trim();
		if (!nome) return;
		try {
			const response = await api.post("/postFabricante", { fabricante: nome });
			if (response.data.status === "Error") {
				setSnackbar({
					children: "Error: Não foi possível adicionar o fabricante",
					severity: "error",
				});
			} else {
				setSnackbar({ children: "Dados salvos com sucesso", severity: "success" });
				createFabricantes({ ...response.data, Funcoes: [] });
			}
		} catch (error) {
			console.error(error);
			setSnackbar({
				children: "Error: Não foi possível se conectar com o servidor",
				severity: "error",
			});
		} finally {
			setFabricante("");
			setOpenAddFabricante(false);
		}
	}

	function cancelarEdicao() {
		setEditar(false);
		setMacros(lastMacros);
		setGroupHosts(lastGroupHosts);
		setTemplates(lastTemplates);
		setSchemaDraft(lastSchemaDraft);
		setSchemaAvancado(false);
		setSchemaAvancadoErro(null);
		setSchemaAvancadoTexto("");
		setDocumentacao(lastDocumentacao);
		if (lastConfig) {
			setSsh(lastConfig.ssh);
			setUserSSH(lastConfig.userSSH);
			setPortaSSH(lastConfig.portaSSH);
			setSenhaSSH(lastConfig.senhaSSH);
			setTelnet(lastConfig.telnet);
			setUserTelnet(lastConfig.userTelnet);
			setPortaTelnet(lastConfig.portaTelnet);
			setSenhaTelnet(lastConfig.senhaTelnet);
		}
	}

	// sugestões de item vêm dos schemas já salvos nos outros modelos
	const sugestoesItensSchema = useMemo(() => {
		const nomes = new Set();
		for (const fab of fabricantes || []) {
			for (const fun of fab.Funcoes || []) {
				for (const mod of fun.Modelos || []) {
					const schema = safeParse(mod?.dashboard_schema, null);
					if (!schema || typeof schema !== "object") continue;
					for (const abaKey of Object.keys(schema)) {
						if (abaKey === "_version") continue;
						const slots = schema[abaKey];
						if (!slots || typeof slots !== "object" || Array.isArray(slots)) continue;
						for (const valor of Object.values(slots)) {
							const itens = Array.isArray(valor) ? valor : [valor];
							for (const item of itens) {
								if (typeof item === "string" && item.trim()) nomes.add(item.trim());
							}
						}
					}
				}
			}
		}
		return [...nomes].sort((a, b) => a.localeCompare(b));
	}, [fabricantes]);

	const patchModelosAposBulk = (resultado) => {
		const aplicados = new Map(
			(resultado?.itens || [])
				.filter((item) => item.status === "aplicado" && item.schemaFinal)
				.map((item) => [item.modeloId, item.schemaFinal])
		);
		if (aplicados.size === 0) return;

		setFabricantes((prev) =>
			prev.map((fab) => ({
				...fab,
				Funcoes: (fab.Funcoes || []).map((fun) => ({
					...fun,
					Modelos: (fun.Modelos || []).map((mod) =>
						aplicados.has(mod.id)
							? { ...mod, dashboard_schema: aplicados.get(mod.id) }
							: mod
					),
				})),
			}))
		);
	};

	// --------- handlers do TemplateForm ---------
	const handleCancelEdit = () => {
		if (hasUnsavedChanges) {
			pendingNavigationRef.current = cancelarEdicao;
			setOpenExitModal(true);
		} else {
			cancelarEdicao();
		}
	};

	const renderTemplateForm = (showHeader = true) => (
		<TemplateForm
			showHeader={showHeader}
			nomeModelo={nomeModelo}
			fabricanteNome={modeloSelecionado?.fabricante?.fabricante}
			funcaoNome={modeloSelecionado?.funcao?.funcao}
			editar={editar}
			salvando={salvando}
			activeTab={activeTab}
			setActiveTab={setActiveTab}
			onEdit={() => setEditar(true)}
			onSave={handleEditaPredefinicoes}
			onCancel={handleCancelEdit}
			groupHosts={groupHosts}
			setGroupHosts={setGroupHosts}
			templates={templates}
			setTemplates={setTemplates}
			templatesList={templatesList}
			macros={macros}
			handleMacroChange={handleMacroChange}
			addMacro={addMacro}
			removeMacro={removeMacro}
			ssh={ssh}
			setSsh={setSsh}
			userSSH={userSSH}
			setUserSSH={setUserSSH}
			portaSSH={portaSSH}
			setPortaSSH={setPortaSSH}
			senhaSSH={senhaSSH}
			setSenhaSSH={setSenhaSSH}
			telnet={telnet}
			setTelnet={setTelnet}
			userTelnet={userTelnet}
			setUserTelnet={setUserTelnet}
			portaTelnet={portaTelnet}
			setPortaTelnet={setPortaTelnet}
			senhaTelnet={senhaTelnet}
			setSenhaTelnet={setSenhaTelnet}
			schemaDraft={schemaDraft}
			setSchemaDraft={setSchemaDraft}
			dashboardSchema={dashboardSchema}
			schemaAvancado={schemaAvancado}
			setSchemaAvancado={setSchemaAvancado}
			schemaAvancadoTexto={schemaAvancadoTexto}
			setSchemaAvancadoTexto={setSchemaAvancadoTexto}
			schemaAvancadoErro={schemaAvancadoErro}
			setSchemaAvancadoErro={setSchemaAvancadoErro}
			sugestoesItensSchema={sugestoesItensSchema}
			onAbrirBulkSchema={() => setOpenBulkSchema(true)}
			notify={setSnackbar}
			documentacao={documentacao}
			setDocumentacao={setDocumentacao}
		/>
	);

	// --------- handlers de Funções (modo edição do sumário do Fabricante) ---------
	const handleCreateFuncaoFromSummary = async (text) => {
		if (!fabricanteSelecionado) return null;
		try {
			const resp = await api.post("/postFuncao", {
				funcao: text,
				fabricanteId: fabricanteSelecionado.id,
			});
			if (resp.data?.status === "Error") {
				setSnackbar({
					children: "Erro ao criar Função",
					severity: "error",
				});
				return null;
			}
			const novo = { ...resp.data, Modelos: [] };
			setFabricantes((prev) =>
				prev.map((f) =>
					f.id === fabricanteSelecionado.id
						? { ...f, Funcoes: [...(f.Funcoes || []), novo] }
						: f
				)
			);
			return novo;
		} catch (e) {
			setSnackbar({
				children: "Erro de conexão com o servidor",
				severity: "error",
			});
			return null;
		}
	};

	const handleEditFuncaoFromSummary = async (id, text) => {
		try {
			const resp = await api.put("/putFuncao", { id, funcao: text });
			if (resp.data?.status === "Error") {
				setSnackbar({
					children: "Erro ao editar Função",
					severity: "error",
				});
				return false;
			}
			setFabricantes((prev) =>
				prev.map((f) => ({
					...f,
					Funcoes: (f.Funcoes || []).map((fun) =>
						fun.id === id ? { ...fun, funcao: text } : fun
					),
				}))
			);
			return true;
		} catch (e) {
			setSnackbar({
				children: "Erro de conexão com o servidor",
				severity: "error",
			});
			return false;
		}
	};

	const handleDeleteFuncaoFromSummary = async (id) => {
		try {
			const resp = await api.post("/deleteFuncao", { id });
			if (resp.data?.status === "Error") {
				setSnackbar({
					children: "Erro ao excluir Função",
					severity: "error",
				});
				return false;
			}
			setFabricantes((prev) =>
				prev.map((f) => ({
					...f,
					Funcoes: (f.Funcoes || []).filter((fun) => fun.id !== id),
				}))
			);
			// se a função excluída era a selecionada, limpa
			if (selectedFuncaoId === id) {
				setSelectedFuncaoId(null);
			}
			return true;
		} catch (e) {
			setSnackbar({
				children: "Erro de conexão com o servidor",
				severity: "error",
			});
			return false;
		}
	};

	const handleReorderFuncoesFromSummary = async (items) => {
		if (!fabricanteSelecionado || !Array.isArray(items)) return false;
		// otimista: aplica a nova ordem localmente antes da resposta
		const orderMap = new Map(items.map((it) => [it.id, it.ordem]));
		const fabId = fabricanteSelecionado.id;
		setFabricantes((prev) =>
			prev.map((f) => {
				if (f.id !== fabId) return f;
				const reordered = [...(f.Funcoes || [])]
					.map((fun) =>
						orderMap.has(fun.id)
							? { ...fun, ordem: orderMap.get(fun.id) }
							: fun
					)
					.sort(
						(a, b) =>
							(a.ordem ?? 0) - (b.ordem ?? 0) ||
							new Date(a.createAt) - new Date(b.createAt)
					);
				return { ...f, Funcoes: reordered };
			})
		);
		try {
			const resp = await api.put("/putOrdemFuncoes", { items });
			if (resp.data?.status === "Error") {
				setSnackbar({
					children: "Erro ao reordenar Funções",
					severity: "error",
				});
				return false;
			}
			return true;
		} catch (e) {
			setSnackbar({
				children: "Erro de conexão com o servidor",
				severity: "error",
			});
			return false;
		}
	};

	// --------- handlers de Modelos (modo edição do sumário da Função) ---------
	const handleCreateModeloFromSummary = async (text) => {
		if (!funcaoSelecionada) return null;
		const funId = funcaoSelecionada.funcao.id;
		try {
			const resp = await api.post("/postModelo", {
				modelo: text,
				funcaoId: funId,
			});
			if (resp.data?.status === "Error") {
				setSnackbar({
					children: "Erro ao criar Modelo",
					severity: "error",
				});
				return null;
			}
			const novo = resp.data;
			setFabricantes((prev) =>
				prev.map((f) => ({
					...f,
					Funcoes: (f.Funcoes || []).map((fun) =>
						fun.id === funId
							? {
								...fun,
								Modelos: [...(fun.Modelos || []), novo],
							}
							: fun
					),
				}))
			);
			if (novo?.Predefinicoes) {
				createPredefinicoes(novo.Predefinicoes);
			}
			return novo;
		} catch (e) {
			setSnackbar({
				children: "Erro de conexão com o servidor",
				severity: "error",
			});
			return null;
		}
	};

	const handleEditModeloFromSummary = async (id, text) => {
		try {
			const resp = await api.put("/putModelo", { id, modelo: text });
			if (resp.data?.status === "Error") {
				setSnackbar({
					children: "Erro ao editar Modelo",
					severity: "error",
				});
				return false;
			}
			setFabricantes((prev) =>
				prev.map((f) => ({
					...f,
					Funcoes: (f.Funcoes || []).map((fun) => ({
						...fun,
						Modelos: (fun.Modelos || []).map((mod) =>
							mod.id === id ? { ...mod, modelo: text } : mod
						),
					})),
				}))
			);
			return true;
		} catch (e) {
			setSnackbar({
				children: "Erro de conexão com o servidor",
				severity: "error",
			});
			return false;
		}
	};

	const handleDeleteModeloFromSummary = async (id) => {
		try {
			const resp = await api.post("/deleteModelo", { id });
			if (resp.data?.status === "Error") {
				setSnackbar({
					children: "Erro ao excluir Modelo",
					severity: "error",
				});
				return false;
			}
			let removedPredefId = null;
			setFabricantes((prev) =>
				prev.map((f) => ({
					...f,
					Funcoes: (f.Funcoes || []).map((fun) => ({
						...fun,
						Modelos: (fun.Modelos || []).filter((mod) => {
							if (mod.id === id) {
								removedPredefId = mod?.Predefinicoes?.id;
								return false;
							}
							return true;
						}),
					})),
				}))
			);
			if (removedPredefId && predefinicaoId === removedPredefId) {
				setPredefinicaoId("");
				setNomeModelo("selecione o modelo");
			}
			return true;
		} catch (e) {
			setSnackbar({
				children: "Erro de conexão com o servidor",
				severity: "error",
			});
			return false;
		}
	};

	const handleReorderModelosFromSummary = async (items) => {
		if (!funcaoSelecionada || !Array.isArray(items)) return false;
		const orderMap = new Map(items.map((it) => [it.id, it.ordem]));
		const funId = funcaoSelecionada.funcao.id;
		setFabricantes((prev) =>
			prev.map((f) => ({
				...f,
				Funcoes: (f.Funcoes || []).map((fun) => {
					if (fun.id !== funId) return fun;
					const reordered = [...(fun.Modelos || [])]
						.map((mod) =>
							orderMap.has(mod.id)
								? { ...mod, ordem: orderMap.get(mod.id) }
								: mod
						)
						.sort(
							(a, b) =>
								(a.ordem ?? 0) - (b.ordem ?? 0) ||
								new Date(a.createAt) - new Date(b.createAt)
						);
					return { ...fun, Modelos: reordered };
				}),
			}))
		);
		try {
			const resp = await api.put("/putOrdemModelos", { items });
			if (resp.data?.status === "Error") {
				setSnackbar({
					children: "Erro ao reordenar Modelos",
					severity: "error",
				});
				return false;
			}
			return true;
		} catch (e) {
			setSnackbar({
				children: "Erro de conexão com o servidor",
				severity: "error",
			});
			return false;
		}
	};

	// --------- decide o que renderizar no painel direito ---------
	// - Fabricante selecionado → sumário do fabricante (com edição inline de Funções)
	// - Função selecionada    → sumário da função (com edição inline de Modelos)
	// - Modelo selecionado    → TemplateForm (modo leitura mostra tudo + tabs;
	//                            modo edição habilita os campos)
	const renderRightPanel = (showHeader = true) => {
		if (fabricanteSelecionado) {
			return (
				<FabricanteSummary
					fabricante={fabricanteSelecionado}
					onCreateFuncao={handleCreateFuncaoFromSummary}
					onEditFuncao={handleEditFuncaoFromSummary}
					onDeleteFuncao={handleDeleteFuncaoFromSummary}
					onReorderFuncoes={handleReorderFuncoesFromSummary}
					onDirtyChange={setFuncoesSummaryDirty}
					resetSignal={summaryResetSignal}
					notify={(msg, severity = "success") =>
						setSnackbar({ children: msg, severity })
					}
				/>
			);
		}
		if (funcaoSelecionada) {
			return (
				<FuncaoSummary
					funcao={funcaoSelecionada.funcao}
					fabricanteNome={funcaoSelecionada.fabricante?.fabricante}
					onCreateModelo={handleCreateModeloFromSummary}
					onEditModelo={handleEditModeloFromSummary}
					onDeleteModelo={handleDeleteModeloFromSummary}
					onReorderModelos={handleReorderModelosFromSummary}
					onDirtyChange={setModelosSummaryDirty}
					resetSignal={summaryResetSignal}
					notify={(msg, severity = "success") =>
						setSnackbar({ children: msg, severity })
					}
				/>
			);
		}
		return renderTemplateForm(showHeader);
	};

	// --------- fechar drawer no mobile com checagem de dirty ---------
	const closeMobileDrawer = () => {
		handleNavigation(() => {
			setPredefinicaoId("");
			setNomeModelo("selecione o modelo");
			setSelectedFabricanteId(null);
			setSelectedFuncaoId(null);
			setEditar(false);
		});
	};

	return (
		<Box sx={styles.paper}>
			<Grid
				container
				spacing={1}
				columns={{ xs: 8, sm: 8, md: 12, lg: 12, xl: 12 }}
			>
				{/* SIDEBAR */}
				<Grid item xs={12} sm={12} md={4} lg={4}>
					<Paper
						elevation={theme.palette.mode === "dark" ? 0 : 3}
						sx={{
							...styles.paperFluxo,
							height: "83vh",
							display: "flex",
							flexDirection: "column",
						}}
					>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								p: 1,
							}}
						>
							<Typography variant="subtitle2" fontSize={"18px"} color="primary">
								Predefinições
							</Typography>
							<Button
								size="small"
								variant="outlined"
								onClick={() => setOpenAddFabricante(true)}
								startIcon={<Add />}
							>
								Fabricante
							</Button>
						</Box>

						<Box sx={{ px: 1, pb: 1 }}>
							<TextField
								id="fabricante-search"
								size="small"
								label="Buscar fabricante / função / modelo"
								variant="outlined"
								value={filtro}
								onChange={(event) => setFiltro(event.target.value)}
								fullWidth
								autoComplete="off"
								sx={styles.searchField}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<Search fontSize="small" />
										</InputAdornment>
									),
									endAdornment: filtro ? (
										<InputAdornment position="end">
											<IconButton size="small" onClick={() => setFiltro("")}>
												<Clear fontSize="small" />
											</IconButton>
										</InputAdornment>
									) : null,
								}}
							/>
						</Box>

						<Stack
							direction="row"
							justifyContent="flex-end"
							sx={{ px: 1, pb: 1 }}
						>
							<Button
								size="small"
								variant="outlined"
								disabled={allExpandedIds.length === 0}
								onClick={handleToggleAll}
								startIcon={
									allExpanded ? <UnfoldLess /> : <UnfoldMore />
								}
							>
								{allExpanded ? "Recolher todos" : "Expandir todos"}
							</Button>
						</Stack>

						<Divider />

						<Box sx={{ flex: 1, overflowY: "auto", p: 1 }}>
							{loading ? (
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										height: "100%",
									}}
								>
									<LoaderThreeDots />
								</Box>
							) : (
								<Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
									{fabricantesFiltrados.map((node) => (
										<HierarchyNode
											key={node.id}
											node={node}
											levelIdx={0}
											ctx={treeCtx}
										/>
									))}
								</Box>
							)}
						</Box>
					</Paper>
				</Grid>

				{/* MAIN — só no desktop. No mobile vira Drawer. */}
				{!isMobile && (
					<Grid item xs={12} sm={12} md={8} lg={8}>
						<Paper
							elevation={theme.palette.mode === "dark" ? 0 : 3}
							sx={{
								...styles.paperFluxo,
								height: "83vh",
								pt: 2,
								px: 2,
								position: "relative",
								overflow: "hidden",
							}}
						>
							{(() => {
								const showLoader = loading || selectionLoading;
								const showEmpty = !showLoader && !selecionado;
								const showContent = !showLoader && selecionado;

								return (
									<Box
										sx={{ position: "relative", width: "100%", height: "100%" }}
									>
										<Fade
											in={showLoader}
											timeout={{ enter: 150, exit: 250 }}
											unmountOnExit
										>
											<Box
												sx={{
													position: "absolute",
													inset: 0,
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
												}}
											>
												<LoaderThreeDots />
											</Box>
										</Fade>

										<Fade
											in={showEmpty}
											timeout={{ enter: 250, exit: 150 }}
											unmountOnExit
										>
											<Box sx={{ ...styles.emptyStateBox, position: "absolute", inset: 0 }}>
												<Typography
													color="text.disabled"
													fontSize={isMobile ? "18px" : "22px"}
												>
													Selecione um modelo
												</Typography>
												<Typography
													variant="body2"
													color="text.disabled"
													textAlign="center"
													sx={{ maxWidth: 360 }}
												>
													As predefinições e o schema do dashboard aparecerão aqui
													assim que um modelo for escolhido.
												</Typography>
											</Box>
										</Fade>

										<Fade
											in={showContent}
											timeout={{ enter: 250, exit: 150 }}
											unmountOnExit
										>
											<Box
												sx={{
													position: "absolute",
													inset: 0,
													overflowY: "auto",
													display: "flex",
													flexDirection: "column",
												}}
											>
												{renderRightPanel(true)}
											</Box>
										</Fade>
									</Box>
								);
							})()}
						</Paper>
					</Grid>
				)}
			</Grid>

			{/* DRAWER (apenas mobile) */}
			<Drawer
				anchor="right"
				open={isMobile && selecionado}
				onClose={closeMobileDrawer}
				PaperProps={{ sx: { width: "100%", maxWidth: "100vw" } }}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						p: 1.5,
						borderBottom: `1px solid ${theme.palette.divider}`,
					}}
				>
					<IconButton
						onClick={closeMobileDrawer}
						edge="start"
						sx={{ mr: 1 }}
					>
						<Clear />
					</IconButton>
					<Stack direction="row" gap={1} alignItems="center" sx={{ flexGrow: 1, minWidth: 0 }}>
						<Typography color="primary" fontWeight={500} fontSize={16}>
							{fabricanteSelecionado
								? "Fabricante:"
								: funcaoSelecionada
									? "Função:"
									: "Modelo:"}
						</Typography>
						<Typography
							fontWeight={500}
							fontSize={16}
							sx={{
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
							}}
						>
							{fabricanteSelecionado
								? fabricanteSelecionado.fabricante
								: funcaoSelecionada
									? funcaoSelecionada.funcao?.funcao
									: nomeModelo}
						</Typography>
					</Stack>
				</Box>

				<Box
					sx={{
						flexGrow: 1,
						overflowY: "auto",
						p: 1,
						position: "relative",
					}}
				>
					{(() => {
						const showLoader = loading || selectionLoading;
						return (
							<Box sx={{ position: "relative", minHeight: "100%" }}>
								<Fade in={showLoader} timeout={{ enter: 150, exit: 250 }} unmountOnExit>
									<Box
										sx={{
											position: "absolute",
											inset: 0,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
										}}
									>
										<LoaderThreeDots />
									</Box>
								</Fade>
								<Fade in={!showLoader} timeout={{ enter: 250, exit: 150 }} unmountOnExit>
									<Box>{renderRightPanel(false)}</Box>
								</Fade>
							</Box>
						);
					})()}
				</Box>
			</Drawer>

			{/* Modal: adicionar fabricante */}
			<Dialog open={openAddFabricante} onClose={() => setOpenAddFabricante(false)}>
				<DialogTitle color="primary">Adicionar fabricante</DialogTitle>
				<DialogContent>
					<TextField
						id="fabricante"
						variant="standard"
						label="Fabricante"
						value={fabricante}
						autoFocus
						onChange={(event) => setFabricante(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === "Enter") {
								event.preventDefault();
								handleApiCriaFabricante();
							}
						}}
						sx={{ width: "240px" }}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						color="secondary"
						onClick={() => {
							setOpenAddFabricante(false);
							setFabricante("");
						}}
					>
						Cancelar
					</Button>
					<Button color="primary" onClick={handleApiCriaFabricante}>
						Confirmar
					</Button>
				</DialogActions>
			</Dialog>

			{/* Aplicar o schema deste modelo em vários modelos */}
			<SchemaBulkApply
				open={openBulkSchema}
				onClose={() => setOpenBulkSchema(false)}
				fabricantes={fabricantes}
				sourceModeloId={modeloId}
				sourceSchema={denormalizeSchema(schemaDraft)}
				schemaNaoSalvo={
					schemaDraftKey(schemaDraft) !== schemaDraftKey(lastSchemaDraft)
				}
				nomeModelo={nomeModelo}
				notify={setSnackbar}
				onAplicado={patchModelosAposBulk}
				styles={styles}
			/>

			{/* Modal: alterações não salvas */}
			<InternoModalConfirmacaoSaida
				openConfirmacao={openExitModal}
				setOpenConfirmacao={setOpenExitModal}
				execute={confirmExit}
				cancel={cancelExit}
				severity="warning"
				title="Atenção"
				headerText="Você tem alterações não salvas."
				contentText="Se continuar, todas as alterações serão descartadas."
				confirmText="Descartar e continuar"
			/>

			{!!snackbar && (
				<Snackbar
					open
					onClose={() => setSnackbar(null)}
					autoHideDuration={2000}
					anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				>
					<Alert {...snackbar} onClose={() => setSnackbar(null)} />
				</Snackbar>
			)}
		</Box>
	);
}
